import { HttpMethod } from '@inpart-io/devtools';
import { type IDataObject, type IDataObjectIdentity, type Operator, type OrderBy, type Query, queryBuilder } from '@inpart-io/devtools-iap';
import { STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { type Module } from 'vuex';

import { useDataObjectHelper } from '@/composables/useDataObjectHelper';
import { store } from '@/store';

import { PAGINATION_DEFAULT_ORDER_BY, PAGINATION_DEFAULT_ROWS_PER_PAGE, PAGINATION_LOADING_DELAY } from '../composables/pagination.constants';

// Actions
export const ADD_ITEMS = 'addItems';
export const FETCH_UNFILTERED_COUNT = 'fetchUnfilteredCount';
export const FETCH_PAGE = 'fetchPage';
export const REMOVE_ITEMS = 'removeItems';
export const REFRESH_PAGE = 'refreshPage';
export const SET_ORDER_BY = 'setOrderBy';
export const SET_PAGE = 'setPage';
export const SET_PAGE_SIZE = 'setPageSize';
export const SET_PAGINATION = 'setPagination';
export const SET_PROPERTIES = 'setProperties';
export const SET_QUERY = 'setQuery';
export const SET_UNFILTERED_QUERY = 'setUnfilteredQuery';
export const SET_VISIBLE_COLUMNS = 'setVisibleColumns';
export const SET_LAYOUT = 'setLayout';
export const SET_UNFILTERED_COUNT = 'setUnfilteredCount';

// Getters
export const HAS_COMBINED_RESULTS = 'hasCombinedResults';
export const GET_CURRENT_PAGE = 'getCurrentPage';
export const GET_ERROR = 'getError';
export const GET_LOADING = 'getLoading';
export const GET_ORDER_BY = 'getOrderBy';
export const GET_PAGE_COUNT = 'getPageCount';
export const GET_PAGE_RESULTS = 'getPageResult';
export const GET_PAGE_SIZE = 'getPageSize';
export const GET_TOTAL = 'getTotal';
export const GET_UNFILTERED_COUNT = 'getUnfilteredCount';
export const GET_QUERY = 'getQuery';
export const GET_UNFILTERED_QUERY = 'getUnfilteredQuery';
export const GET_LAYOUT = 'getLayout';
export const GET_VISIBLE_COLUMNS = 'getVisibleColumns';

export type DataObjectIdentity = Omit<IDataObjectIdentity, 'queryScore'>;

export interface PaginatedSearch {
    currentPage: number;
    dataObjectType: string;
    dataObjectTypes?: string[]; // Handles requests that return multiple types of Data Objects
    error?: string;
    loading: boolean;
    orderBy: OrderBy[];
    pageSize: number; // Zero means no pagination
    properties: string;
    query: Query;
    results: DataObjectIdentity[];
    timestamp?: number;
    total: number;
    unfilteredQuery: Query;
    unfilteredCount: number;
    visibleColumns: string[];
    layout: string;
}

export type PaginatedSearchDefaults = Partial<
    { types?: string[] } & Pick<PaginatedSearch, 'orderBy' | 'pageSize' | 'properties' | 'query' | 'unfilteredQuery'>
>;

export type PaginationState = PaginatedSearch;

const { getDataObjectModule, getDataObjectManager } = useDataObjectHelper();

/**
 * This function is used to create and register a new Pagination module in case it doesn't already exist.
 *
 * @export
 * @param {string} moduleKey
 * @param {string} dataObjectType
 * @param { Partial<PaginatedSearch>} [params]
 */
export function createPaginationModule(moduleKey: string, dataObjectType: string, params?: PaginatedSearchDefaults): void {
    if (!store.hasModule(moduleKey)) {
        store.registerModule(moduleKey, getPaginationModuleInstance(dataObjectType, params));
    }
}
/**
 * This function creates a pagination module with default settings and an initial state.
 *
 * @param {string} _dataObjectType
 * @param {string} [_properties]
 * @return {*}  {Module<PaginationState, unknown>}
 */
function getPaginationModuleInstance(_dataObjectType: string, params?: PaginatedSearchDefaults): Module<PaginationState, unknown> {
    // Initialize with default values
    const {
        orderBy: _orderBy = [PAGINATION_DEFAULT_ORDER_BY],
        pageSize: _pageSize = PAGINATION_DEFAULT_ROWS_PER_PAGE,
        properties: _properties = '*',
        query: _query = {},
        unfilteredQuery: _unfilteredQuery = {},
        types: _dataObjectTypes = undefined
    } = params ?? {};
    return {
        namespaced: true,
        state: () => {
            return {
                currentPage: 1,
                dataObjectType: _dataObjectType,
                dataObjectTypes: _dataObjectTypes,
                layout: null,
                loading: false,
                orderBy: _orderBy,
                pageSize: _pageSize,
                properties: _properties,
                query: _query,
                results: [],
                total: 0,
                unfilteredCount: 0,
                unfilteredQuery: _unfilteredQuery,
                visibleColumns: []
            };
        },
        mutations: {
            SET_CURRENT_PAGE(state, { page }: { page: number }) {
                state.currentPage = page > 0 ? page : 1;
            },
            SET_ERROR(state, { error }: { error: string }) {
                state.error = error;
            },
            SET_LOADING(state, { loading }: { loading: boolean }) {
                state.loading = loading;
            },
            SET_ORDER_BY(state, { orderBy }: { orderBy: OrderBy[] }) {
                state.orderBy = orderBy;
            },
            SET_PAGE_SIZE(state, { pageSize }: { pageSize: number }) {
                state.pageSize = pageSize;
            },
            SET_PROPERTIES(state, { properties }: { properties: string }) {
                state.properties = properties;
            },
            SET_QUERY(state, { query }: { query: Query }) {
                state.query = query;
            },
            SET_RESULTS(state, { results = [] }: { results: DataObjectIdentity[] }) {
                state.results = results;
            },
            SET_TOTAL(state, { total }: { total: number }) {
                state.total = total > 0 ? total : 0;
            },
            SET_UNFILTERED_COUNT(state, { unfilteredCount }: { unfilteredCount: number }) {
                state.unfilteredCount = unfilteredCount > 0 ? unfilteredCount : 0;
            },
            SET_UNFILTERED_QUERY(state, { query }: { query: Query }) {
                state.unfilteredQuery = query;
            },
            SET_TIMESTAMP(state, { timestamp }: { timestamp: number | null }) {
                state.timestamp = timestamp;
            },
            SET_VISIBLE_COLUMNS(state, { visibleColumns }: { visibleColumns: string[] }) {
                state.visibleColumns = visibleColumns;
            },
            SET_DATA_OBJECTS_TYPES(state, { dataObjectTypes }: { dataObjectTypes: string[] }) {
                state.dataObjectTypes = dataObjectTypes;
            },
            SET_LAYOUT(state, { layout }: { layout: string }) {
                state.layout = layout;
            }
        },
        actions: {
            [SET_UNFILTERED_COUNT]({ commit }, { unfilteredCount }: { unfilteredCount: number }): void {
                commit('SET_UNFILTERED_COUNT', { unfilteredCount });
            },
            [ADD_ITEMS]({ commit, state }, { toAdd }: { toAdd: DataObjectIdentity[] }): void {
                const { results = [], total = 0, unfilteredCount = 0 } = state;
                if ((toAdd?.length ?? 0) > 0) {
                    commit('SET_RESULTS', { results: [...toAdd, ...results] });
                    commit('SET_TOTAL', { total: total + toAdd.length });
                    commit('SET_UNFILTERED_COUNT', { unfilteredCount: unfilteredCount + toAdd.length });
                }
            },
            [REMOVE_ITEMS]({ dispatch, commit, state, getters }, { toRemove = [] }: { toRemove: DataObjectIdentity[] }): void {
                const { results: currentResults = [], total = 0, unfilteredCount = 0, currentPage } = state;
                if ((toRemove?.length ?? 0) > 0) {
                    // 1. Remove the element from the current list of results
                    const filteredResults = currentResults.filter((item) => {
                        return !toRemove.some((target) => {
                            return item.dobjId === target.dobjId && item.type === target.type;
                        });
                    });
                    commit('SET_RESULTS', {
                        results: filteredResults
                    });
                    commit('SET_TOTAL', { total: total - toRemove.length });
                    commit('SET_UNFILTERED_COUNT', { unfilteredCount: unfilteredCount - toRemove.length });

                    // 2. Fill the current page with the missing results
                    const page = filteredResults.length > 0 ? currentPage : currentPage - 1;
                    const fetchDelay = getters[HAS_COMBINED_RESULTS] ? PAGINATION_LOADING_DELAY : 0;
                    void dispatch(SET_PAGE, { page, toExclude: toRemove, fetchDelay });
                }
            },
            async [FETCH_UNFILTERED_COUNT]({ commit, state }): Promise<number> {
                const { dataObjectType, unfilteredQuery, dataObjectTypes } = state;
                const manager = getDataObjectManager(dataObjectType);
                const unfilteredCount = await manager.count(unfilteredQuery, null, null, dataObjectTypes);
                commit('SET_UNFILTERED_COUNT', { unfilteredCount });
                return unfilteredCount;
            },
            async [FETCH_PAGE](
                { commit, dispatch, state, getters },
                { toExclude = [], fetchDelay = 0 }: { toExclude?: DataObjectIdentity[]; fetchDelay?: number }
            ): Promise<DataObjectIdentity[]> {
                const { query, pageSize, currentPage, orderBy, properties, dataObjectType, dataObjectTypes } = state;
                const manager = getDataObjectManager(dataObjectType);

                try {
                    const startIndex = pageSize * (currentPage - 1);
                    // To prevent recently removed elements in the result list (because of indexing delays) we'll enforce the exclusion
                    // with an additional operator IS_NOT_IN with the removed data objects ids
                    let pageQuery: Query;
                    if (toExclude.length > 0) {
                        const excludeOperatorList: Operator[] = [];
                        if (getters[HAS_COMBINED_RESULTS]) {
                            for (const item of toExclude) {
                                excludeOperatorList.push(
                                    queryBuilder.op.not(
                                        queryBuilder.op.and(queryBuilder.cmp.refersTo('dobjId', item.dobjId), queryBuilder.cmp.thisTypeIs(item.type))
                                    )
                                );
                            }
                        } else {
                            const excludeIds = toExclude.map((item) => {
                                return item.dobjId;
                            });
                            excludeOperatorList.push(queryBuilder.op.and(queryBuilder.cmp.isNotIn('dobjId', excludeIds)));
                        }
                        pageQuery = queryBuilder.build(queryBuilder.op.and(query.operator, ...excludeOperatorList), startIndex, pageSize, orderBy);
                    } else {
                        pageQuery = { ...query, startIndex, maxResults: pageSize, orderBy };
                    }

                    commit('SET_LOADING', { loading: true });

                    await new Promise((resolve) => {
                        setTimeout(resolve, fetchDelay);
                    });

                    const response = await manager.searchWithCount(pageQuery, { properties }, HttpMethod.POST, null, dataObjectTypes);

                    const dataObjectList = response?.content ?? [];

                    void dispatch(STORE_DATA_OBJECTS, { dataObjectList });

                    const retrievedIdentityList = dataObjectList.map((dataObject) => {
                        return { type: dataObject['@type'], dobjId: dataObject.dobjId };
                    });

                    commit('SET_RESULTS', { results: retrievedIdentityList });

                    commit('SET_TOTAL', { total: response?.total });
                    commit('SET_TIMESTAMP', { timestamp: Date.now() });
                    commit('SET_ERROR', { error: null });

                    return retrievedIdentityList;
                } catch (error) {
                    commit('SET_ERROR', { error: error.message });
                } finally {
                    commit('SET_LOADING', { loading: false });
                }
            },
            [STORE_DATA_OBJECTS]({ dispatch, state, getters }, { dataObjectList }: { dataObjectList: IDataObject[] }) {
                const { dataObjectType } = state;

                for (const dataObject of dataObjectList) {
                    if (typeof dataObject.transientFields === 'object' && dataObject.transientFields !== null && 'queryScore' in dataObject.transientFields) {
                        dataObject._queryScore_ = dataObject.transientFields.queryScore;
                    }
                }

                if (getters[HAS_COMBINED_RESULTS]) {
                    const groupedByType: Record<string, IDataObject[]> = {};
                    // Analyze the list of result to push them into their particular stores
                    dataObjectList.forEach((item) => {
                        const groupKey = item['@type']; // Group results by @type
                        if (!groupedByType[groupKey]) {
                            groupedByType[groupKey] = [];
                        }
                        groupedByType[groupKey].push(item);
                    });

                    Object.entries(groupedByType).forEach(([key, dataObjectListByType]) => {
                        const dataObjectModule = getDataObjectModule(key);
                        void dispatch(`${dataObjectModule}/${STORE_DATA_OBJECTS}`, { dataObjectList: dataObjectListByType }, { root: true });
                    });
                } else {
                    const dataObjectModule = getDataObjectModule(dataObjectType);
                    void dispatch(`${dataObjectModule}/${STORE_DATA_OBJECTS}`, { dataObjectList }, { root: true });
                }
            },
            [SET_PAGINATION](
                { commit, dispatch },
                {
                    page,
                    pageSize,
                    query,
                    orderBy,
                    properties,
                    fetch = true,
                    dataObjectTypes
                }: { page?: number; pageSize?: number; query?: Query; orderBy?: OrderBy[]; properties?: string; fetch: boolean; dataObjectTypes?: string[] }
            ) {
                if (page !== undefined) {
                    commit('SET_CURRENT_PAGE', { page });
                }

                if (pageSize !== undefined) {
                    commit('SET_PAGE_SIZE', { pageSize });
                }

                if (query !== undefined) {
                    commit('SET_QUERY', { query });
                    commit('SET_CURRENT_PAGE', { page: 1 });
                }

                if (orderBy !== undefined) {
                    commit('SET_ORDER_BY', { orderBy });
                }

                if (dataObjectTypes?.length) {
                    commit('SET_DATA_OBJECTS_TYPES', { dataObjectTypes });
                }

                if (properties) {
                    commit('SET_PROPERTIES', { properties });
                }

                if (fetch) {
                    void dispatch(FETCH_PAGE, {});
                }
            },
            [SET_ORDER_BY]({ commit, dispatch }, { orderBy }: { orderBy: OrderBy[] }) {
                commit('SET_ORDER_BY', { orderBy });
                void dispatch(FETCH_PAGE, {});
            },
            [SET_PAGE](
                { commit, dispatch },
                { page, toExclude = [], fetchDelay = 0 }: { page: number; toExclude?: DataObjectIdentity[]; fetchDelay?: number }
            ) {
                commit('SET_CURRENT_PAGE', { page });
                void dispatch(FETCH_PAGE, { toExclude, fetchDelay });
            },
            [SET_PAGE_SIZE]({ commit, dispatch }, { pageSize }: { pageSize: number }) {
                commit('SET_PAGE_SIZE', { pageSize });
                void dispatch(FETCH_PAGE, {});
            },
            [SET_PROPERTIES]({ commit, dispatch }, { properties }: { properties: string }) {
                commit('SET_PROPERTIES', { properties });
                void dispatch(FETCH_PAGE, {});
            },
            [SET_QUERY]({ commit, dispatch }, { query }: { query: Query }) {
                commit('SET_QUERY', { query });
                commit('SET_CURRENT_PAGE', { page: 1 });
                void dispatch(FETCH_PAGE, {});
            },
            [SET_UNFILTERED_QUERY]({ commit, dispatch }, { query }: { query: Query }) {
                commit('SET_UNFILTERED_QUERY', { query });
                void dispatch(FETCH_UNFILTERED_COUNT);
            },
            [REFRESH_PAGE]({ commit, dispatch }) {
                commit('SET_TIMESTAMP', { timestamp: null });
                void dispatch(FETCH_PAGE, {});
                void dispatch(FETCH_UNFILTERED_COUNT);
            },
            [SET_VISIBLE_COLUMNS]({ commit }, { visibleColumns }: { visibleColumns: string[] }) {
                commit('SET_VISIBLE_COLUMNS', { visibleColumns });
            },
            [SET_LAYOUT]({ commit }, { layout }: { layout: string }) {
                commit('SET_LAYOUT', { layout });
            }
        },
        getters: {
            [HAS_COMBINED_RESULTS]: (state) => {
                return (state.dataObjectTypes?.length ?? 0) > 0;
            },
            [GET_CURRENT_PAGE]: (state) => {
                return state.currentPage;
            },
            [GET_PAGE_SIZE]: (state) => {
                return state.pageSize;
            },
            [GET_TOTAL]: (state) => {
                return state.total;
            },
            [GET_UNFILTERED_COUNT]: (state) => {
                return state.unfilteredCount;
            },
            [GET_ORDER_BY]: (state) => {
                return state.orderBy;
            },
            [GET_PAGE_COUNT]: (state) => {
                const { total = 0, pageSize } = state;
                return pageSize > 0 ? Math.ceil(total / pageSize) : 1;
            },
            [GET_PAGE_RESULTS]: (state) => {
                const { results = [], pageSize } = state;
                return pageSize > 0 ? results.slice(0, pageSize) : results;
            },
            [GET_QUERY]: (state) => {
                return state.query;
            },
            [GET_UNFILTERED_QUERY]: (state) => {
                return state.unfilteredQuery;
            },
            [GET_LOADING]: (state) => {
                return state.loading;
            },
            [GET_ERROR]: (state) => {
                return state.error;
            },
            [GET_LAYOUT]: (state) => {
                return state.layout;
            },
            [GET_VISIBLE_COLUMNS]: (state) => {
                return state.visibleColumns;
            }
        }
    };
}
